import { Button } from '../../../../../core/components/atoms/Button';
import { Icon } from '../../../../../core/components/atoms/Icon';
import { Typography } from '../../../../../core/components/molecules/Typography';
import { IPracticeEnemDaySummary } from '../../../model/Enem';
import {
  DAY_AREAS,
  DAY_LABELS,
  formatDayDuration,
} from '../../../utils/enemDay';

interface PracticeEnemDayCardProps {
  summary: IPracticeEnemDaySummary;
  onStart: (day: 1 | 2) => void;
  onFeedback: (day: 1 | 2) => void;
}

/**
 * Card de um dos dois dias na tela de escolha.
 *
 * Diferente do DayCard do simuladão-evento, aqui não existe portão nem contagem
 * regressiva para abrir: os dois dias ficam sempre disponíveis. O único estado
 * que importa é o da tentativa — não iniciada, em andamento ou concluída.
 */
export const PracticeEnemDayCard = ({
  summary,
  onStart,
  onFeedback,
}: PracticeEnemDayCardProps) => {
  const { day, totalQuestions, durationInMinutes, isStarted, isFinished } =
    summary;

  const duration = formatDayDuration(durationInMinutes);

  const buildStatusChip = () => {
    if (isFinished) {
      return (
        <div className='flex w-fit items-center gap-sm rounded-sm bg-feedback-color-success-pure px-2'>
          <Typography size='small' className='text-[#fff]'>
            Concluído
          </Typography>
        </div>
      );
    }

    if (isStarted) {
      return (
        <div className='flex w-fit items-center gap-sm rounded-sm bg-feedback-color-warning-pure px-2'>
          <Typography size='small' className='text-[#fff]'>
            Em andamento
          </Typography>
        </div>
      );
    }

    return null;
  };

  return (
    <li className='flex flex-1 flex-col justify-between gap-xl rounded-md bg-color-lightest p-6 shadow-level-1'>
      <div className='flex flex-col gap-md'>
        {buildStatusChip()}

        <Typography type='title' size='h3'>
          {day}º dia
        </Typography>

        <Typography size='default' className='text-mono-color-dark'>
          {DAY_LABELS[day]}
        </Typography>

        <ul className='flex flex-col gap-xs pt-2'>
          {DAY_AREAS[day].map((area) => (
            <li key={`day${day}-${area}`} className='flex items-center gap-sm'>
              <Icon
                Icon='check'
                type='material'
                className='text-md !text-brand-color-primary-pure'
              />
              <Typography size='small'>{area}</Typography>
            </li>
          ))}
        </ul>

        <div className='flex flex-row flex-wrap gap-lg pt-2'>
          <div className='flex items-center gap-sm'>
            <Icon
              Icon='list_alt'
              type='material'
              className='text-md text-mono-color-dark'
            />
            <Typography size='small'>{totalQuestions} questões</Typography>
          </div>
          {duration && (
            <div className='flex items-center gap-sm'>
              <Icon
                Icon='schedule'
                type='material'
                className='text-md text-mono-color-dark'
              />
              <Typography size='small'>{duration} de prova</Typography>
            </div>
          )}
        </div>

        {summary.lastAttempt && isFinished && (
          <div className='flex flex-row flex-wrap gap-lg pt-2'>
            <span className='flex flex-row items-center gap-sm'>
              <Icon
                Icon='check'
                type='material'
                className='text-md !text-feedback-color-success-pure'
              />
              <Typography size='small'>
                {summary.lastAttempt.correct} acertos
              </Typography>
            </span>
            <span className='flex flex-row items-center gap-sm'>
              <Icon
                Icon='close'
                type='material'
                className='text-md !text-incidencia-n5'
              />
              <Typography size='small'>
                {summary.lastAttempt.incorrect} erros
              </Typography>
            </span>
          </div>
        )}
      </div>

      <div className='flex flex-col gap-sm'>
        <Button
          full
          variant={isFinished ? 'outlined' : 'contained'}
          LeftIcon={isStarted && !isFinished ? 'play_arrow' : undefined}
          iconType={isStarted && !isFinished ? 'material' : undefined}
          onClick={() => onStart(day)}
        >
          {isFinished
            ? `Refazer ${day}º dia`
            : isStarted
              ? 'Continuar prova'
              : `Iniciar ${day}º dia`}
        </Button>

        {isFinished && (
          <Button
            full
            variant='text'
            LeftIcon='fact_check'
            iconType='material'
            onClick={() => onFeedback(day)}
          >
            Ver gabarito do {day}º dia
          </Button>
        )}
      </div>
    </li>
  );
};

export default PracticeEnemDayCard;
